import { useEffect, useState } from 'react';
import { Button } from '../../../../components/Button';
import { DateField } from '../../../../components/inputs/DateField';
import { Select } from '../../../../components/inputs/Select';
import { TextField } from '../../../../components/inputs/TextField';
import { Modal } from '../../../../components/Modal';
import { useToast } from '../../../../components/toastContext';
import { ApiError } from '../../../../lib/apiError';
import { focusValidationError } from '../../../../lib/validationFocus';
import { applicantsApi } from '../applicantsApi';
import type { ApplicantListItem } from '../applicantTypes';
import { sampleInterview } from '../utils/sampleRecruitment';

const ID_PREFIX = 'applicant-interview';

/** `RecruitmentFieldRules.InterviewPlaceMaxLength` — a typed place longer than the column is cut, not refused. */
const PLACE_MAX_LENGTH = 300;

/**
 * Schedules one QUALIFIED applicant's **Initial Interview** — the third stage of the vacancy's cycle
 * (product call, 19 Oct 2026): where, and when. All three required. The date is NOT capped at today
 * (an interview usually lies ahead); only one before the qualifying screening is refused.
 *
 * The time is a TIME OF DAY, never an instant — `HH:mm` in, `HH:mm:00` on the wire, as Press Con.
 */
export function ApplicantInterviewModal({
  applicant,
  applicantName,
  onClose,
  onSaved,
}: {
  applicant: ApplicantListItem;
  applicantName: string;
  onClose: () => void;
  onSaved: (saved: ApplicantListItem) => void;
}) {
  const toast = useToast();

  // Seeded from what is scheduled — a reschedule opens on the current one.
  const [place, setPlace] = useState(applicant.interviewPlace ?? '');
  const [date, setDate] = useState(applicant.interviewDate ?? '');
  const [time, setTime] = useState(applicant.interviewTime ? applicant.interviewTime.slice(0, 5) : '');
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [places, setPlaces] = useState<string[]>([]);
  const [placesLoading, setPlacesLoading] = useState(true);

  useEffect(() => {
    const controller = new AbortController();

    // A failed load leaves the list empty; a place can still be typed.
    applicantsApi
      .interviewPlaces(controller.signal)
      .then((next) => {
        if (!controller.signal.aborted) setPlaces(next);
      })
      .catch(() => undefined)
      .finally(() => {
        if (!controller.signal.aborted) setPlacesLoading(false);
      });

    return () => controller.abort();
  }, []);

  const valid = place.trim().length > 0 && date.length > 0 && time.length > 0;
  const canSave = valid && confirmed && !saving;

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    try {
      const saved = await applicantsApi.scheduleInterview(applicant.id, {
        interviewPlace: place.trim(),
        interviewDate: date,
        interviewTime: `${time}:00`,
      });
      toast.success('Interview scheduled.');
      onSaved(saved);
    } catch (caught) {
      const failure = caught instanceof ApiError ? caught : new ApiError('Could not schedule the interview.', 0);
      setError(failure);
      focusValidationError(failure, { idPrefix: ID_PREFIX });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={applicant.interviewDate != null ? 'Reschedule initial interview' : 'Schedule initial interview'}
      size="lg"
      id={`${ID_PREFIX}-modal`}
      autogenerate={{
        onGenerate: () => {
          const sample = sampleInterview();
          setPlace(sample.place);
          setDate(sample.date);
          setTime(sample.time);
        },
        disabled: saving,
      }}
      confirm={{ id: `${ID_PREFIX}-confirm`, checked: confirmed, onChange: setConfirmed, disabled: saving }}
      footer={
        <>
          <Button id={`${ID_PREFIX}-cancel`} variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            id={`${ID_PREFIX}-submit`}
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel="Submitting"
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        <p className="text-xs text-slate-500">
          Interviewing <span className="text-slate-700">{applicantName}</span>, screened Qualified.
        </p>

        {/*
          A CUSTOM-ADDING DROPDOWN (product call, 19 Oct 2026) — the places interviews are already set
          for, or a new one typed in. Suggestions, not a catalog: the value is the place as written.
        */}
        <Select
          id={`${ID_PREFIX}-interview-place`}
          label="Place"
          required
          description="Where the interview is held — a room, a building, or a meeting link. Pick one already used, or type a new one."
          placeholder="Choose or type a place"
          value={place}
          onChange={(next) => setPlace(next ?? '')}
          options={[
            ...(place && !places.includes(place) ? [{ value: place, label: place }] : []),
            ...places.map((p) => ({ value: p, label: p })),
          ]}
          remote
          loading={placesLoading}
          allowCustom
          onCreateOption={(raw) => setPlace(raw.trim().slice(0, PLACE_MAX_LENGTH))}
          error={place.trim() ? error?.fieldError('interviewPlace') : 'Enter where the interview is held.'}
          disabled={saving}
        />

        <div className="grid gap-4 sm:grid-cols-2">
          <DateField
            id={`${ID_PREFIX}-interview-date`}
            label="Date"
            required
            description="The day of the interview — not before the screening."
            min={applicant.screenedDate ?? undefined}
            value={date}
            onChange={setDate}
            error={error?.fieldError('interviewDate')}
            disabled={saving}
          />

          <TextField
            id={`${ID_PREFIX}-interview-time`}
            label="Time"
            required
            type="time"
            value={time}
            onChange={(e) => setTime(e.target.value)}
            error={time ? error?.fieldError('interviewTime') : 'Enter the time of the interview.'}
            disabled={saving}
          />
        </div>
      </div>
    </Modal>
  );
}
